import { device, sendAdtracking, getClickPosition, getParams2Native } from '../../common/util'
import { setClickUrl, setNoticeUrl } from '../../common/tool'
import { setStorekitStatus } from '../../common/report'
import * as nativeCalledListener from '../../api/nativeCalledListener'
import * as nativeComponentApi from '../../api/nativeComponentAPI'
import * as nativeApi from '../../api/index'
import App from "../App";
import {_compareVersion, _getPureSDKVersion} from "../../common/m_base/util";
import {reportNonBizData} from "../../common/m_common/mNonBizReport";

// 通用业务平台类
class Platform {
  constructor() {
  }

  static isAndroid() {
    return device().isAndroid
  }

  static isIOS() {
    return device().isiOS
  }

  static getDevice() {
    return device().isiOS ? 'ios' : 'android'
  }

  /**
   * 判断currentSDKVersion版本号与要比较的targetVersion版本号之间的大小关系
   * @param currentSDKVersion
   * @param targetVersion
   * @returns {number} 1：currentVersion 大于 targetVersion  |  -1：currentVersion 小于 targetVersion  |  0：currentVersion 等于 targetVersion
   */
  static checkVersion(currentSDKVersion, targetVersion) {
    if (currentSDKVersion.length === 0) {
      // console.warn('本地获取的 SDKVersion 为空，推测为首次 init 场景', currentSDKVersion)
      return NaN
    }

    let diff = 0;
    diff += (currentSDKVersion[0] - targetVersion[0]) << 20
    diff += (currentSDKVersion[1] - targetVersion[1]) << 10
    diff += (currentSDKVersion[2] - targetVersion[2])
    if (diff > 0) {
      return 1
    } else if (diff == 0) {
      return 0
    } else {
      return -1
    }
  }

  /**
   * 获取本次存储的版本号，以 string Array 格式返回
   * @returns {string[]|*[]}
   */
  static getCurrentVersion() {
    let currentSDKVersion = App
      && App.initData
      && (App.initData.sdk_info || App.initData.appSetting
        && App.initData.appSetting.sdk_info) || ''
    console.log("currentSDKVersion:", currentSDKVersion)

    // 模板页首次加载还未通过 init 获取 SDK 版本号时，本地存储的 SDKVersion 始终为 ''
    // 这种情况下不需要处理直接返回空数组即可
    if (currentSDKVersion === '') {
      return []
    }

    // 特殊兼容: 由于安卓某些版本可能返回 sdk_info: "MAL_9.6.0,3.0.1" 的形式, 所以需要对这种不规则的版本格式进行兼容
    // 正则表达式 ((?<=_)).+(?=(,)) 可以匹配到MAL_9.6.0,3.0.1中的9.6.0，即目标SDK版本。
    if (device().isAndroid) {
      // 移除 , 后缀
      currentSDKVersion = currentSDKVersion.split(',')[0]
      // 移除渠道标识
      currentSDKVersion = this.removeAndroidChannelSuffix(currentSDKVersion);
    }
    // 清除多余前后缀
    let arrSDKVer = currentSDKVersion.split('_')[1].split('.')
    return arrSDKVer
  }

  /**
   * 把版本号最后一位 1/2 的渠道标识去掉，如果 minor 版本号只有一位，则不作处理，直接返回传入的版本号
   * @param currentSDKVersion 未移除渠道标识的版本号
   * @returns {string} 移除了渠道标识的版本号
   */
  static removeAndroidChannelSuffix(currentSDKVersion) {
    if (currentSDKVersion === '') return currentSDKVersion

    /**
     * 注意:
     * 安卓的版本号有一个特殊的规则, 如果测试包为 12.2.0, 则 release 包的版本号会存在两个 12.2.01 和 12.2.02,
     * 最后额外附加的数字 1 和 2 代表 SDK 投放的渠道，1 用来发国内(12.2.01), 2 用来发国外(12.2.02),
     * 所以这里判断安卓版本号的时候, 需要把最后一位 1/2 的渠道标识去掉
     */
    let tmpVersion = currentSDKVersion.slice(0, -1)
    if (tmpVersion.slice(-1) === '.') {
      // console.warn('当前版本号后缀只有一位, 推测应该是安卓测试包, 不再尝试截取', {currentSDKVersion, tmpVersion})
    } else {
      // console.warn('当前版本号后缀大于 1 位，应该是安卓渠道包, 尝试截取', {currentSDKVersion, tmpVersion})
      currentSDKVersion = tmpVersion
    }
    return currentSDKVersion;
  }

  static getVersionByString(versionString) {
    console.log("versionString", versionString)
    let version = versionString.split('.').map(item => Number(item))
    return version
  }

  /**
   * 判断当前sdk版本号是否大于targetVersion
   * @param targetVersion
   * @returns {boolean}
   */
  static checkVersionGreaterThan(targetVersion) {
    const currentVersion = this.getCurrentVersion()
    targetVersion = this.getVersionByString(targetVersion)
    return this.checkVersion(currentVersion, targetVersion) === 1
  }

  /**
   * 判断当前sdk版本号是否小于targetVersion
   * @param targetVersion
   * @returns {boolean}
   */
  static checkVersionLessThan(targetVersion) {
    const currentVersion = this.getCurrentVersion()
    targetVersion = this.getVersionByString(targetVersion)
    return this.checkVersion(currentVersion, targetVersion) === -1
  }

  /**
   * 判断当前sdk版本号是否等于targetVersion
   * @param targetVersion
   * @returns {boolean}
   */
  static checkVersionEqualTo(targetVersion) {
    const currentVersion = this.getCurrentVersion()
    targetVersion = this.getVersionByString(targetVersion)
    return this.checkVersion(currentVersion, targetVersion) === 0
  }

  /**
   * 判断当前sdk版本号是否大于等于targetVersion
   * @param targetVersion
   * @returns {boolean}
   */
  static checkVersionGreaterThanOrEqualTo(targetVersion) {
    return this.checkVersionGreaterThan(targetVersion) || this.checkVersionEqualTo(targetVersion)

  }

  /**
   * 判断当前sdk版本号是否小于等于targetVersion
   * @param targetVersion
   * @returns {boolean}
   */
  static checkVersionLessThanOrEqualTo(targetVersion) {
    return this.checkVersionLessThan(targetVersion) || this.checkVersionEqualTo(targetVersion)
  }

  /**
   * 判断当前sdk版本号是否大于minVersion并且小于maxVersion （闭区间）
   * @param targetVersion
   * @returns {boolean}
   */
  static checkVersionBetweenClosedInterval(minVersion, maxVersion) {
    return this.checkVersionGreaterThan(minVersion) && this.checkVersionLessThan(maxVersion)
  }

  /**
   * 判断当前sdk版本号是否大于等于minVersion并且小于等于maxVersion （开区间）
   * @param targetVersion
   * @returns {boolean}
   */
  static checkVersionBetweenOpenInterval(minVersion, maxVersion) {
    return this.checkVersionGreaterThanOrEqualTo(minVersion) && this.checkVersionLessThanOrEqualTo(maxVersion)
  }

  /**
   * 判断当前sdk版本号是否大于等于minVersion并且小于maxVersion （半开区间）
   * @param targetVersion
   * @returns {boolean}
   */
  static checkVersionBetweenHalfOpenInterval(minVersion, maxVersion) {
    return this.checkVersionGreaterThanOrEqualTo(minVersion) && this.checkVersionLessThan(maxVersion)
  }

  /**
   * 广播
   * @param eventName
   * @param data
   * @returns {Promise<any>}
   */
  broadcast(eventName, data) {
    return nativeApi.broadcast(eventName, data)
  }

  /**
   * 监听广播
   * @param bindEventName
   * @param cb
   */
  onBroadcast(bindEventName, cb) {
    return nativeApi.onBroadcast(bindEventName, cb)
  }

  // 从 SDK 获取上下文环境数据
  fetchInitData(params = { pageNo: 1, exclude_ids: [] }) {
    return nativeApi.fetchInitData(params)
  }

  // load 阶段通知 SDK "H5 页面已经 ready", 可以展示(show) 了
  readyToShowAD() {
    return nativeApi.readyToShowAD();
  }

  // load 阶段通知 SDK "H5 页面没有 ready"
  failureToShowAD({ errMessage, errDetail } = {}) {
    return nativeApi.failureToShowAD({ errMessage, errDetail })
  }

  onAdShow(cb) {
    return nativeApi.onAdShow(cb)
  }

  //由于banner需求sdk端无法给开发者webviewshow回调的bug，临时由h5自己发送dowebviewshow事件来模拟之前sdk发送的webviewshow事件
  //该方法在新的sdk版本中不会使用
  onAdShowTemp(cb) {
    return nativeApi.onAdShowTemp(cb)
  }

  /**
   * 向 SDK 询问 1 ~ n 个广告素材下载/解压到本地之后的存储地址 (file: 协议)
   * @param {String} unitId - 广告位 id
   * @param {Object[]} resource - 要获取的资源描述列表
   * @param {Number} resource[].type - 资源的类型（1:视频，2:web素材地址）
   * @param {String} resource[].ref - 下发的文件远程地址 或者其他可以唯一标示本地资源的索引
   * @returns {*|Promise<any>} 资源在本地存储的路径 (file: 协议)
   */
  getFileInfoForRvIv({ unitId, resource }) {
    return nativeApi.VideoBridgeGetFileInfo({ unitId, resource })
  }

  closeAd(unitId) {
    return nativeApi.closeAd(unitId)
  }

  // 从 SDK 获取上下文环境数据
  bannerFetchInitData(params = { pageNo: 1, exclude_ids: [] }) {
    return nativeApi.bannerFetchInitData(params)
  }

  // load 阶段通知 SDK "H5 页面已经 ready", 可以展示(show) 了
  bannerReadyToShowAD() {
    return nativeApi.bannerReadyToShowAD()
  }

  // load 阶段通知 SDK "H5 页面没有 ready"
  bannerFailureToShowAD({ errMessage, errDetail } = {}) {
    return nativeApi.bannerFailureToShowAD({ errMessage, errDetail })
  }

  /**
   * js 通知SDK发送用具体UA上报url
   * @param {Object} params
   * @param {Number} params.type campaign.onlyImpressionUrl,
   * @param {String} params.url campaign.impressionUA
   */
  reportUrl(params = { type: 1, url: '' }) {
    return nativeApi.reportUrl(params)
  }

  /**
   * js通知SDK 在客户端发出请求,iOS SDK从v5.6.0 Banner版本开始支持，所有广告类型都支持
   *  数组元素:  {"url":"www.mintegral.com", "type":2}
   *  要求SDK 以type类型的uaType来请求改url
   * type 为可选，目前Impression 一定有，其他的不一定有
   */
  reportUrls(params = [{ type: 1, url: '' }, { type: 1, url: '' }]) {
    return nativeApi.reportUrls(params)
  }

  sendImpressions(params = []) {
    return nativeApi.sendImpressions(params)
  }

  // 显示 native 关闭按钮
  showNativeCloseBtn() {
    return nativeApi.showNativeCloseBtn()
  }

  // 隐藏 native 关闭按钮
  hideNativeCloseBtn() {
    return nativeApi.hideNativeCloseBtn()
  }

  //设置 native 关闭按钮显示状态
  setNativeCloseBtnDisplay(state) {
    return nativeApi.setNativeCloseBtnDisplay(state)
  }

  // Banner广告被点击
  bannerClick(params) {
    return nativeApi.bannerClick(params)
  }

  //	获取网络状态
  getNetstat(params) {
    return nativeApi.getNetstat(params)
  }

  //	网络状态变更时通知
  onNetstatChanged(cb) {
    return nativeApi.onNetstatChanged(cb)
  }

  /**
   * 向 SDK 询问 1 ~ n 个广告素材下载/解压到本地之后的存储地址 (file: 协议)
   * @param {String} unitid 广告位 id
   * @param {Number} type //资源的类型（1:视频，2:web素材地址, 4:img）
   * @param {String} ref //下发的文件远程地址 或者其他可以唯一标示本地资源的索引
   * @returns {*|Promise<any>} 资源在本地存储的路径 (file: 协议)
   */
  getVideoPath({ unitId, ref }) {
    return nativeApi.getVideoPath({ unitId, ref })
  }

  //视图展示时通知
  onViewAppeared(cb) {
    return nativeApi.onViewAppeared(cb)
  }

  //视图隐藏时通知
  onViewDisappeared(cb) {
    return nativeApi.onViewDisappeared(cb)
  }

  /**
   * 开发者更改页面元素样式时触发
   * @param actionHandle | Function
   */
  onNativeSetStyle(actionHandle) {
    nativeCalledListener.onNativeSetStyle(actionHandle)
  }

  /**
   * 开发者更改页面元素样式时触发
   * @param actionHandle | Function
   */
  onNativeSetStyleList(actionHandle) {
    nativeCalledListener.onNativeSetStyleList(actionHandle)
  }

  /**
   * 开发者通知展示关闭按钮时触发
   * @param actionHandle | Function
   */
  onNativeShowCloseButton(actionHandle) {
    nativeCalledListener.onNativeShowCloseButton(actionHandle)
  }

  /**
   * 开发者通知隐藏关闭按钮时触发
   * @param actionHandle | Function
   */
  onNativeHideCloseButton(actionHandle) {
    nativeCalledListener.onNativeHideCloseButton(actionHandle)
  }

  /**
   * 开发者更改音量设置时触发
   * @param actionHandle | Function
   */
  onNativeSetVolume(actionHandle) {
    nativeCalledListener.onNativeSetVolume(actionHandle)
  }

  /**
   * 开发者更改视频播放器播放模式时触发
   * @param actionHandle | Function
   */
  onNativeSetVideoPlayMode(actionHandle) {
    nativeCalledListener.onNativeSetVideoPlayMode(actionHandle)
  }

  /**
   * 创建一个webview组件
   * @param params
   * @returns {*|Promise<any>}
   */
  createWebview(params = {}) {
    return nativeComponentApi.createWebview(params)
  }

  /**
   * 创建视频播放组件
   * @param params
   * @returns {*|Promise<any>}
   */
  createPlayerView(params = {}) {
    return nativeComponentApi.createPlayerView(params)
  }

  /**
   * 创建StoreKit视图
   * @param params
   * @returns {*|Promise<any>}
   */
  createStoreKitView(params = {}) {
    return nativeComponentApi.createStoreKitView(params)
  }

  /**
   * 创建旧的视频播放模板组件
   * @param params
   * @returns {*|Promise<any>}
   */
  createSubPlayTemplateView(params = {}) {
    return nativeComponentApi.createSubPlayTemplateView(params)
  }

  /**
   * 创建旧的endcard组件
   * @param params
   * @returns {*|Promise<any>}
   */
  createEndCard(params = {}) {
    return nativeComponentApi.createEndCard(params)
  }

  /**
   * 创建OM组件
   * @param params
   * @returns {*|Promise<any>}
   */
  createOM(params = {}) {
    return nativeComponentApi.destroyComponent(params)
  }

  /**
   * 销毁组件
   * @param params
   * @returns {*|Promise<any>}
   */
  destoryComponent(params = {}) {
    return nativeComponentApi.destroyComponent(params)
  }

  /**
   * action：3，获取视图的信息
   * @param params
   * @returns {*|Promise<any>}
   */
  getComponentOptions(params = {}) {
    return nativeComponentApi.getComponentOptions(params)
  }

  /**
   * action：4，设置视图的信息
   * @param params
   * @returns {*|Promise<any>}
   */
  setComponentOptions(params = {}) {
    return nativeComponentApi.setComponentOptions(params)
  }

  /**
   * action：5，修改视图frame
   * @param params
   * @returns {*|Promise<any>}
   */
  setViewRect(params = {}) {
    return nativeComponentApi.setViewRect(params)
  }

  /**
   * action：6，从父视图移除视图（默认未释放该对象）
   * @param params
   * @returns {*|Promise<any>}
   */
  removeFromSuperView(params = {}) {
    return nativeComponentApi.removeFromSuperView(params)
  }

  /**
   * 在当前视图上添加其他视图
   * @param params
   * @returns {*|Promise<any>}
   */
  appendSubView(params = {}) {
    return nativeComponentApi.appendSubView(params)
  }

  /**
   * 将当前视图添加到指定视图上
   * @param params
   * @returns {*|Promise<any>}
   */
  appendViewTo(params = {}) {
    return nativeComponentApi.appendViewTo(params)
  }

  /**
   * 移动当前视图到父视图的顶部
   * @param params
   * @returns {*|Promise<any>}
   */
  bringViewToFront(params = {}) {
    return nativeComponentApi.bringViewToFront(params)
  }

  /**
   * 隐藏视图
   * @param params
   * @returns {*|Promise<any>}
   */
  hideView(params = {}) {
    return nativeComponentApi.hideView(params)
  }

  /**
   * 显示视图
   * @param params
   * @returns {*|Promise<any>}
   */
  showView(params = {}) {
    return nativeComponentApi.showView(params)
  }

  /**
   * 设置视图的背景颜色
   * @param params
   * @returns {*|Promise<any>}
   */
  setViewBgColor(params = {}) {
    return nativeComponentApi.setViewBgColor(params)
  }

  /**
   * 设置视图的透明度
   * @param params
   * @returns {*|Promise<any>}
   */
  setViewAlpha(params = {}) {
    return nativeComponentApi.setViewAlpha(params)
  }

  /**
   * 设置视图的缩放
   * @param params
   * @returns {*|Promise<any>}
   */
  setViewScale(params = {}) {
    return nativeComponentApi.setViewScale(params)
  }

  /**
   * 将组件(sourceComponent)添加到目标组件(targetComponent)的上面
   * @param sourceComponentId | Number 添加的组件id
   * @param targetComponentId | Number 目标组件id
   * @param rect | Object 相对于父视图的坐标及宽高
   */
  insertAboveTo(params = {}) {
    return nativeComponentApi.insertAboveTo(params)
  }

  /**
   * 将组件(sourceComponent)添加到目标组件(targetComponent)的下面
   * @param sourceComponentId | Number 添加的组件id
   * @param targetComponentId | Number 目标组件id
   * @param rect | Object 相对于父视图的坐标及宽高
   */
  insertBelowTo(params = {}) {
    return nativeComponentApi.insertBelowTo(params)
  }

  /**
   * webview加载url
   * @param params
   * @returns {*|Promise<any>}
   */
  webviewLoad(params = {}) {
    return nativeComponentApi.webviewLoad(params)
  }

  /**
   * reload
   * @param params
   * @returns {*|Promise<any>}
   */
  webviewReload(params = {}) {
    return nativeComponentApi.webviewReload(params)
  }

  /**
   * goback
   * @param params
   * @returns {*|Promise<any>}
   */
  webviewGoBack(params = {}) {
    return nativeComponentApi.webviewGoBack(params)
  }

  /**
   * goforward
   * @param params
   * @returns {*|Promise<any>}
   */
  webviewGoForward(params = {}) {
    return nativeComponentApi.webviewGoForward(params)
  }

  /**
   * webviewFireEvent
   * @param params
   * @returns {*|Promise<any>}
   */
  webviewFireEvent(params = {}) {
    return nativeComponentApi.webviewFireEvent(params)
  }

  /**
   * 原生视频-播放
   * @param params
   * @returns {*|Promise<any>}
   */
  nativeVideoPlay(params = {}) {
    return nativeComponentApi.nativeVideoPlay(params)
  }

  /**
   * 原生视频-暂停
   * @param params
   * @returns {*|Promise<any>}
   */
  nativeVideoPause(params = {}) {
    return nativeComponentApi.nativeVideoPause(params)
  }

  /**
   * 原生视频-恢复播放
   * @param params
   * @returns {*|Promise<any>}
   */
  nativeVideoResume(params = {}) {
    return nativeComponentApi.nativeVideoResume(params)
  }

  /**
   * 原生视频-停止
   * @param params
   * @returns {*|Promise<any>}
   */
  nativeVideoStop(params = {}) {
    return nativeComponentApi.nativeVideoStop(params)
  }

  /**
   * 原生视频-设置为静音
   * @param params
   * @returns {*|Promise<any>}
   */
  nativeVideoMute(params = {}) {
    return nativeComponentApi.nativeVideoMute(params)
  }

  /**
   * 原生视频-设置为非静音
   * @param params
   * @returns {*|Promise<any>}
   */
  nativeVideoUnmute(params = {}) {
    return nativeComponentApi.nativeVideoUnmute(params)
  }

  /**
   * action：205，原生视频-替换视频源
   * @param params
   * @returns {*|Promise<any>}
   */
  nativeVideoReplaceSource(params = {}) {
    return nativeComponentApi.nativeVideoReplaceSource(params)
  }

  /**
   * 预加载storeKit
   * @param params
   * @returns {*|Promise<any>}
   */
  preloadStoreKit(params = {}) {
    console.error('未实现抽象方法：preloadStoreKit')
  }

  /**
   * 添加stk到父视图
   * @param params
   * @returns {*|Promise<any>}
   */
  addStoreKitTo(params = {}) {
    console.error('未实现抽象方法：addStoreKitTo')
  }

  /**
   * 预加载SubPlayTemplateView
   * @param params
   * @returns {*|Promise<any>}
   */
  preloadSubPlayTemplateView(params = {}) {
    return nativeComponentApi.preloadSubPlayTemplateView(params)
  }

  /**
   * 设置SubPlayTemplateView信息
   * @param params
   * @returns {*|Promise<any>}
   */
  setSubPlayTemplateInfo(params = {}) {
    return nativeComponentApi.setSubPlayTemplateInfo(params)
  }

  /**
   * 全屏展示
   * @param params
   * @returns {*|Promise<any>}
   */
  fullScreenShow(componentId, rootWebviewId, rect = { top: 0, left: 0, right: 0, bottom: 0 }) {
    return nativeComponentApi.fullScreenShow(componentId, rootWebviewId, rect)
  }

  /**
   * offer被展示，操作设备持久化存放offer展示频次表(RV/IV广告类型)
   * @param params {Object} campaign信息和unitId(安卓独有)，通过this.platform.prepareDataForIncOfferFreRvIv()获得
   */
  increaseOfferFrequenceForRvIv(params) {
    return nativeApi.increaseOfferFrequenceForRvIv(params)
  }

  /**
   * 为increaseOfferFrequenceForRvIv接口组成要发送的数据
   * @param campaign {Object}
   */
  prepareDataForIncOfferFreRvIv() {
    console.error('未实现抽象方法：prepareDataForIncOfferFreRvIv')
  }

  getInitData() {
    let initData = App && App.initData;
    if (typeof initData === "undefined") {
      console.error('initData undefined，请注意')
      initData = {}
    }
    return initData
  }

  getAppSetting() {
    let initData = this.getInitData();
    let appSetting = initData.appSetting
    if (typeof appSetting === "undefined") {
      console.error('appSetting undefined，请注意')
      appSetting = {}
    }
    return appSetting
  }

  getUnitSetting() {
    let initData = this.getInitData();
    let unitSetting = initData.unitSetting
    if (typeof unitSetting === "undefined") {
      console.error('unitSetting undefined，请注意')
      unitSetting = {}
    }
    return unitSetting
  }

  getCampaignList() {
    let initData = this.getInitData();
    let campaignList = initData.campaignList
    if (typeof campaignList === "undefined") {
      console.error('campaignList undefined，请注意')
      campaignList = []
    }
    return campaignList
  }

  getFirstCampaign() {
    let campaignList = this.getCampaignList();
    let firstCampaign = campaignList[0]
    if (typeof firstCampaign === "undefined") {
      console.error('firstCampaign undefined，请注意')
      firstCampaign = {}
    }
    return firstCampaign
  }

  getOnlyImpressionUrl(campaign) {
    // let firstCampaign = this.getFirstCampaign();
    let onlyImpressionUrl = campaign.only_impression_url
    if (typeof onlyImpressionUrl === "undefined") {
      console.error('onlyImpressionUrl undefined，请注意')
      onlyImpressionUrl = ''
    }
    return onlyImpressionUrl
  }

  getPvUrls() {
    let firstCampaign = this.getFirstCampaign();
    let pvUrls = firstCampaign.pv_urls
    if (typeof pvUrls === "undefined") {
      console.error('pvUrls undefined，请注意')
      pvUrls = []
    }
    return pvUrls
  }

  getIvreward() {
    let ivreward = App && App.initData && App.initData.appSetting && App.initData.appSetting.ivreward;
    if (typeof ivreward === "undefined") {
      console.error('ivreward undefined，请注意')
      ivreward = {}
    }
    return ivreward;
  }

  getIvRewardPlayValue() {
    console.error('未实现抽象方法：getIvRewardPlayValue')
  }

  getIvRewardEnable() {
    console.error('未实现抽象方法：getIvRewardEnable')
  }

  getFilePathByRemoteUrlForRvIv(resParam = { url: '', type: 2 }) {
    console.log('获取 filePath : ')
    let filePath = {
      path: resParam.url
    }
    let resource = [{
      type: resParam.type,
      ref: resParam.url,
    }]

        return new Promise((resolve, reject) => {
      this.getFileInfoForRvIv({
        unitId: App.unit.id,
        resource,
      }).then(filePathConfig => {
        if (filePathConfig && filePathConfig.resource && filePathConfig.resource[0] && filePathConfig.resource[0][resParam.url] && filePathConfig.resource[0][resParam.url].path) {
          filePath = filePathConfig.resource[0][resParam.url]
          console.log('获取 filePath =====================> : ', filePath)
        } else {
          console.warn('获取 filePath 失败: ', { url: resParam.url, filePathConfig })
        }
        resolve(filePath)
            }).catch(err => reject(err))
    })
  }

  setRewardInfo(res) {
    let adShowData = res && res.param;
    let userId = adShowData && adShowData.userId;
    let reward = adShowData && adShowData.reward;
    App.rewardInfo = { userId, reward }
    console.warn('rewardInfo: ', App.rewardInfo, res)
  }

  getRewardInfo() {
    let rewardInfo = App && App.rewardInfo;
    if (typeof rewardInfo !== 'undefined') {
      return rewardInfo
    } else {
      return {}
    }
  }

  /**
   *@desc 回调与扣回调所有逻辑
   *@param {Object} data
   * init返回所有数据，
   * 模版的adShowTime,
   * 当前campaign,
   * convert{Number}，是否奖励回调，1，回调，2，不回调，
   * reward{Object},从webviewshow中获取的奖励信息
   * error{Object} 错误信息对象，与sdk约定
   * nodeName{String},当前时机的名称
   *  showSuccess,广告展示成功
   *  showFailed,广告展示失败
   *  playCompleted,视频播放完成
   *  endcardShowSuccess,endcard展示成功
   *  clicked,只要被点击
   *  dismissed,整个广告关闭（offer关闭，即endcard关闭）
   *@author WangPing
   *@date 2020-06-11
   */
  callReactDeveloper(data) {
    let expired = data.expired;
    let type = 0;
    let unitId = data.unitSetting.unitId ? data.unitSetting.unitId : data.unit_id;
    let convert = data.convert;
    if (convert == 2 && Platform.isIOS() && Platform.checkVersionBetweenOpenInterval("5.9.0", "6.3.0")) {
      convert = 0;
    }
    switch (data.nodeName) {
      case "showSuccess":
        type = 1;
        break;
      case "showFailed":
        type = 2;
        break;
      case "playCompleted":
        type = 3;
        break;
      case "endcardShowSuccess":
        type = 4;
        break;
      case "clicked":
        type = 5;
        break;
      case "dismissed":
        type = 6;
        break;
      default:

    }
    const allCallBack = function () {
      if (type === 2) {
        console.log({
          unitId: unitId,
          type,
          hit: 0,
          expired,
          error: data.error
        })
        nativeApi.VideoBridgeReactDeveloper({
          unitId: unitId,
          type,
          hit: 0,
          expired,
          error: data.error
        })
      } else if (type === 6) {
        console.log({
          unitId: unitId,
          type,
          hit: 0,
          expired,
          convert: convert,
          campaign: data.campaign,
          reward: data.reward
        })
        nativeApi.VideoBridgeReactDeveloper({
          unitId: unitId,
          type,
          hit: 0,
          expired,
          convert: convert,
          campaign: data.campaign,
          reward: data.reward
        })
      } else {
        console.log({
          unitId: unitId,
          type,
          hit: 0,
          expired,
        })
        nativeApi.VideoBridgeReactDeveloper({
          unitId: unitId,
          type,
          hit: 0,
          expired,
        })
      }
    }
    console.log("###回调时机:", type)
    console.log("###回调时机代表的方法名称：", data.nodeName)
    console.log("###回调传入data", data)
    // let hb = data.campaign.hb;
    // console.log("hb",hb)
    // 允许回调
    allCallBack()
  }

  /**
   *@自动load逻辑
   *@param {Object} data - init后返回的所有数据
   *@author WangPing
   *@date 2020-06-11
   */
  callLoadAds(data) {
    // console.log("###调用自动load方法")
    // console.log("###传入的data", data)
    /* let hb = data.campaign.hb;
     console.log("###hb:", hb)
     let unitId = data.unitSetting.unitId ? data.unitSetting.unitId : data.unit_id
     if (!hb) {
         console.log("###非bid，执行自动load")
         let adType = data.campaign.ad_type ? data.campaign.ad_type : data.campaign.adType;
         console.log("###adType:", adType)
         let adTypePramas = 1;
         if (adType == 94) {
             adTypePramas = 1;
             console.log("###rv")
         } else if (adType == 287) {  // iv
             console.log("###iv")
             adTypePramas = 2;
         }
         // 仅rv执行自动load逻辑
         if(adTypePramas === 2){
             return false
         }
         let atlType = data.unitSetting.atl_type; // 定义cbpruleValue数组
         console.log("###获取atlType:", atlType)
         if (atlType && String === typeof atlType) {
             atlType = atlType.split(",");
         }
         let type = 0;
         switch (data.nodeName) {
             case "isReady":
                 type = 1;
                 break;
             case "showSuccess":
                 type = 2;
                 break;
             case "vcnNoReady":
                 type = 3;
                 break;
             case "showFailed":
                 type = 4;
                 break;
             case "playCompleted":
                 type = 5;
                 break;
             case "endcardShowSuccess":
                 type = 6;
                 break;
             case "dismissed":
                 type = 7;
                 break;
             default:

         }
         console.log("###当前时机：", type + ":" + data.nodeName)
         // atlType中的值全转为数字
         if (atlType && atlType.length > 0) {
             atlType = atlType.map((v) => {
                 return Number(v)
             })
         } else {
             console.log("###atlType为空或不存在，走兜底[1,2,3,4]")
             atlType = [1, 2, 3, 4]
         }
         console.log("###转换后atlType===>", atlType)
         console.log("###data.unitSetting.placementid====>", data.unitSetting.placementid)
         // 如果数组中存在当前type，说明需要自动load
         if (atlType.indexOf(type) !== -1) {
             console.log("###需要自动load===当前type存在于atlType中，type:", type)
             console.log("###调用自动load协议")
             nativeApi.VideoBridgeLoadAds({
                 unitId: unitId,
                 type: 1,
                 adtype: adTypePramas,
                 placementId: data.unitSetting.placementid,
             })
         }
     }*/
  }

  rewardJsInstall(params = {}) {
    return nativeApi.RewardJsInstall(params)
  }

  preloadStoreKit(params = {}) {
    console.error('接口preloadStorekit未被实现')
  }

  updateReadyStatusForRvIv(params = { isReady: 1 }) {
    return nativeApi.VideoBridgeReadyStatusHasReady(params)
  }

  addStoreKitTo(params = {}) {
    console.error('接口addStore')
  }

  onSystemPause(cb) {
    return nativeApi.onSystemPause(cb)
  }

  onSystemResume(cb) {
    return nativeApi.onSystemResume(cb)
  }

  onOrientationChange(cb) {
    return nativeApi.orientation(cb)
  }

  getDevMuteSetting(showData) {
    console.error('接口getDevMuteSetting未实现')
  }

  videoBridgeClick(campaign = {}) {
    return nativeApi.VideoBridgeClick(campaign)
  }

  /**
    *@点击跳转
    *@param {Object} opts
      *@param {Boolean} autoClick ,是否为自动点击
      *@param {EnumString} template:点击发生的模板，【'rv' 播放模版，'ec' endcard模版，'banner' banner模版】
      *@param {EnumString} subComp:点击发生的子组件，【'mof' 猜你喜欢，'ctabanner' ctaBanner】
      *@param {Object} campaign
    *@author WANG Ping, QIU Chen
    *@date 2020-09-03
  */
  clickInstall(event, opts) {
    try {
      reportNonBizData({
        reportType: 'btAtmosphereClick_04'
      })
    } catch (e) {

    }
    console.log("点击event", event)
    console.log("点击opts", opts)
    let defaultCampaign = Object.assign({}, opts.campaign || App.initData.campaignList[0] || {})

    // 获取点击位置
    let { clickScreenPosition, windowStartPoint } = getClickPosition(event, opts.autoClick)
    // 如果是自动storekit，为camp设置needStorekit属性
    if (opts.autoClick) {
      defaultCampaign = setStorekitStatus(defaultCampaign, opts.subComp === 'mof')
    }
    // clickurl 广点通坐标宏替换
    defaultCampaign = setClickUrl(defaultCampaign, clickScreenPosition, App.initData)

    // noticeurl坐标宏替换
    defaultCampaign = setNoticeUrl(defaultCampaign, clickScreenPosition, windowStartPoint, false, opts.subComp === 'ctabanner')

    // 未知操作，保留并不进行任何更改
    sendAdtracking(defaultCampaign.click_url)

    // 如果是banner模板需要组装defaultCampaign
    if (opts.template === 'rv' || opts.template === 'banner') {
      defaultCampaign = {
        type: 1,
        pt: defaultCampaign
      }
    }
    try {
      reportNonBizData({
        reportType: 'btAtmosphereClick_05'
      })
    } catch (e) {

    }
    if (opts.template === "rv") {
      try {
        reportNonBizData({
          reportType: 'btAtmosphereClick_05_rv'
        })
      } catch (e) {

      }
      this.videoBridgeClick(defaultCampaign).then(res => {
      }).catch(err => {
        console.error(err)
      })
    } else if (opts.template === "ec" || opts.template === "ecBottom") {
      try {
        reportNonBizData({
          reportType: 'btAtmosphereClick_05_ec'
        })
      } catch (e) {

      }
      this.rewardJsInstall(defaultCampaign).then(res => {
      }).catch(err => {
        console.error(err)
      })
    } else if (opts.template === "banner"){
      try {
        reportNonBizData({
          reportType: 'btAtmosphereClick_05_banner'
        })
      } catch (e) {

      }
      // Android 下根据 SDK 版本判断应该使用新协议 console.info 或是老协议 window.prompt
      // 可以使用新协议的版本：'10.3.0' =< version < '12.0.0' 或 version >= 12.2.0
      // 老协议：其他 version
      let currentSDKVersion = App && App.initData && (App.initData.sdk_info || App.initData.appSetting && App.initData.appSetting.sdk_info) || ''
      let pureSDKVersion = _getPureSDKVersion(currentSDKVersion)
      let isAndroidXVersion = _compareVersion(pureSDKVersion,'12.2.0') >= 0;
      let isAndroidNoXVersion = _compareVersion(pureSDKVersion, '10.3.0') >= 0
        && _compareVersion(pureSDKVersion, '12.0.0') < 0;

      // 如果当前版本不支持 console.info，则提前精简 campaign 中不必要的字段
      if (device().isAndroid && !isAndroidXVersion && !isAndroidNoXVersion) {
        defaultCampaign.pt = getParams2Native(defaultCampaign.pt);
      }

      this.bannerClick(defaultCampaign).then(res => {
      }).catch(err => {
        console.error(err)
      })

    } else if (opts.template === "confirmDialog") {
      try {
        reportNonBizData({
          reportType: 'btAtmosphereClick_05_cd'
        })
      } catch (e) {

      }
      let apkDownloader = opts.apkDownloaderInstance;
      apkDownloader.setNoticeUrl(defaultCampaign.notice_url)
      apkDownloader.setApkUrl(defaultCampaign.click_url)
      apkDownloader.setCurrentCampaign(defaultCampaign)
      apkDownloader.doAction()
    }else {
      try {
        reportNonBizData({
          reportType: 'btAtmosphereClick_05_df'
        })
      } catch (e) {

      }
      this.rewardJsInstall(defaultCampaign).then(res => {
      }).catch(err => {
        console.error(err)
      })
    }
  }




  callReactDeveloperCb(data){
      if(!data.type) {
          console.error('缺少必要参数', data, location.href)
          return false
      }
      const params = Object.assign({
          unitId: App.unit.id,
          type: undefined,
          hit: 0,
          expired: false,
      }, data)
      nativeApi.VideoBridgeReactDeveloper(params)
  }

  callDeveloperShowSuccess(data){
      this.callReactDeveloperCb(Object.assign({
          type: 1
      }, data))
  }
  callDeveloperShowFailed(data){
      if(!data.error){
        console.error('callDeveloperShowFailed --> 缺少必要回调参数', data, location.href)
      }
      this.callReactDeveloperCb(Object.assign({
          type: 2,
          error: 'error'
      }, data))
  }
  callDeveloperPlayCompleted(data){
      this.callReactDeveloperCb(Object.assign({
          type: 3
      }, data))
  }
  callDeveloperEndcardShowSuccess(data){
      this.callReactDeveloperCb(Object.assign({
          type: 4
      }, data))
  }
  callDeveloperClicked(data){
      this.callReactDeveloperCb(Object.assign({
          type: 5
      }, data))
  }
  callDeveloperDismissed(data){
      this.callReactDeveloperCb(Object.assign({
          type: 6,
          convert: 2,
          campaign: null,
          reward: null
      }, data))
  }


}

export default Platform